import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getAllChatMessages,
  markChatAsResolved,
  sendChatMessage,
} from "controller/api";
import type {
  AccessSpecifier,
  ChatResultData,
  GetAllChatInput,
  MarkChatAsResolvedInput,
  SendChatMessageInput,
} from "shared";
import { debugConsole } from "utilz/helpers";

// ─── Imperative page fetch (called from Chat.tsx, not a hook) ────────

export interface FetchChatPageParams {
  serviceType: "ticket" | "task";
  serviceId: string;
  fetcherId: string;
  page: number;
  limit?: number;
  accessSpecifier?: AccessSpecifier;
  filterMarkAsResolved?: boolean;
}

export async function fetchChatPage({
  serviceType,
  serviceId,
  fetcherId,
  page,
  limit,
  accessSpecifier,
  filterMarkAsResolved,
}: FetchChatPageParams): Promise<ChatResultData> {
  return getAllChatMessages({
    serviceType,
    serviceId,
    fetcherId,
    page,
    limit,
    accessSpecifier,
    filterMarkAsResolved,
  });
}

// ─── Window fetch hook (scroll-to-pinned) ────────────────────────────

interface UseWindowFetchParams {
  serviceType: "ticket" | "task";
  serviceId: string;
  fetcherId: string;
  accessSpecifier?: AccessSpecifier;
  filterMarkAsResolved?: boolean;
  messageId?: string;
  enabled?: boolean;
}

export const useFetchMessageWindow = ({
  serviceType,
  serviceId,
  fetcherId,
  accessSpecifier,
  filterMarkAsResolved,
  messageId,
  enabled = false,
}: UseWindowFetchParams) => {
  return useQuery({
    queryKey: ["chat-window", serviceId, messageId],
    queryFn: () =>
      getAllChatMessages({
        serviceType,
        serviceId,
        fetcherId,
        messageId,
        accessSpecifier,
        filterMarkAsResolved,
      }),
    enabled: Boolean(serviceId && messageId && enabled),
  });
};

// ─── Legacy single-page hook (pinned-chat ChatBox) ───────────────────

export const useGetAllChatMessages = ({
  serviceType,
  serviceId,
  fetcherId,
  limit,
  page,
  accessSpecifier,
  filterMarkAsResolved,
  enabled = true,
}: GetAllChatInput & { enabled?: boolean }) => {
  return useQuery({
    queryKey: [
      "chat",
      serviceId,
      limit,
      page,
      accessSpecifier,
      filterMarkAsResolved,
    ],
    queryFn: () =>
      getAllChatMessages({
        serviceType,
        serviceId,
        fetcherId,
        limit,
        page,
        accessSpecifier,
        filterMarkAsResolved,
      }),
    enabled: serviceId && enabled ? true : false,
    onError: (error: unknown) => debugConsole(error),
  });
};

// ─── Mutations ──────────────────────────────────────────────────────

export const useSendChatMessage = () => {
  return useMutation((value: SendChatMessageInput) => sendChatMessage(value), {
    onSuccess(data, variables, context) {
      // queryClient.invalidateQueries(["chat"]);
    },
  });
};

export const useMarkChatAsResolved = () => {
  return useMutation(
    (value: MarkChatAsResolvedInput) => markChatAsResolved(value),
    {
      onSuccess(data, variables, context) {
        // queryClient.invalidateQueries(["chat"]);
      },
    },
  );
};
